import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import Button from 'components/UI/Button';
import { CustomerIdAndBiometricsEnrollment } from 'idmission-web-sdk';
import { useState } from 'react';
import { createIdMissionLog, createIdMissionSession } from 'modules/session/actions';
import { buildIdMissionLogPayload } from 'modules/session/buildCompleteSignupPayload';
import { ReactComponent as CameraIcon } from 'assets/icons/camera.svg';
import { ReactComponent as LocationIcon } from 'assets/icons/geolocation.svg';
import Switch from 'components/UI/Switch';
import { getCameraPermission, getLocationPermission } from 'utils/events';
import platform from 'utils/platform';
import { ID_MISSION_VARS } from 'constants/registration';
import { GA_REGISTRATION_ACTIONS, GA_REGISTRATION_LABELS } from 'constants/gaEvents';
import { ENVIRONMENT_TYPE } from 'constants/common';
import Debugger from 'utils/debugger';
import { getIdMissionLogTiming, ID_MISSION_LOG_TIMING } from '../newClientAuthFlow';
import StepHeader from './StepHeader';
import { getIDMissionResponse } from './getIDMissionResponse';

const FAKE_VALUE = false;

const ClientIdentityValidation = ({
  literals,
  onContinue,
  onError,
  onEvent,
  headerStep,
  headerTotal,
  logOnCapture = false,
}) => {
  const dispatch = useDispatch();
  const [sessionId, setSessionId] = useState(null);
  const [cameraPermissions, setCameraPermissions] = useState(false);
  const [locationPermissions, setLocationPermissions] = useState(false);

  const handleCancel = (type) => {
    onEvent(GA_REGISTRATION_ACTIONS.EVENT, type);
    setSessionId(null);
  };

  const handleError = () => {
    onError(literals.verification.errorIdMissionFailure);
    setSessionId(null);
  };

  const handleCameraAccessDenied = () => {
    // El SDK detecta denegación de cámara después de iniciar — en browser damos
    // contexto específico para que el usuario sepa cómo habilitarla.
    const msg = !platform.isNativeApp
      ? (literals.verification.errorCameraPermissionsBrowser
        || literals.verification.errorCameraPermissions)
      : literals.verification.errorIdMissionFailure;
    onError(msg);
    setSessionId(null);
  };

  const handleComplete = (data, tempSessionId = null) => {
    Debugger.info('handleComplete', data);
    onEvent(
      GA_REGISTRATION_ACTIONS.EVENT,
      GA_REGISTRATION_LABELS.CAPTURE_COMPLETED,
    );
    const { responseCustomerData = {}, resultData = {} } = data || {};
    Debugger.info('IDMission extractedPersonalData (raw):', responseCustomerData?.extractedPersonalData);

    const verification = resultData?.verificationResultDetails || {};

    onEvent(
      GA_REGISTRATION_ACTIONS.EVENT,
      `${GA_REGISTRATION_LABELS.CAPTURE_SCORE}-${resultData?.verificationResult} (${resultData?.verificationResultCode || 'na'})`,
    );

    let error = false;
    if (![ID_MISSION_VARS.APPROVED, ID_MISSION_VARS.CUSTOMER_ONBOARDED, ID_MISSION_VARS.DUPLICATE_CUSTOMER].includes(resultData?.verificationResult)) {
      error = literals.verification.errorsIDMission.Generic;
    }
    Object.keys(verification).forEach((key) => {
      if (key !== 'Decision_Deduplication' && verification[key]?.DecisionResult && verification[key].DecisionResult !== ID_MISSION_VARS.OK) {
        error = literals.verification?.errorsIDMission?.[key] || `${literals.verification.errorsIDMission.Generic2} (${key})`;
      }
    });

    let name;
    const lastNameDoc = responseCustomerData?.extractedPersonalData?.lastName_doc || '';
    const lastnameDoc2 = responseCustomerData?.extractedPersonalData?.lastName2_doc || '';
    const nameDocStr = responseCustomerData?.extractedPersonalData?.name_doc || '';
    // Quitar del name_doc los apellidos para dejar solo nombre(s)
    if (responseCustomerData?.extractedPersonalData?.name_doc?.split(' ').length > 1) {
      name = nameDocStr
        .replace(lastNameDoc, '')
        .replace(lastnameDoc2, '')
        .replace(/\s+/g, ' ')
        .trim();
    } else {
      name = `${responseCustomerData?.extractedPersonalData?.firstName_doc || ''} ${responseCustomerData?.extractedPersonalData?.middleName_doc || ''}`;
    }

    const formatData = {
      id: responseCustomerData?.extractedIdData?.idNumber2 || '',
      idType: responseCustomerData?.extractedIdData?.idType || '',
      curp: responseCustomerData?.extractedIdData?.idNumber1 || '',
      firstName: name || '',
      lastName: `${responseCustomerData?.extractedPersonalData?.lastName_doc || ''} ${responseCustomerData?.extractedPersonalData?.lastName2_doc || ''}`,
      birthDate: responseCustomerData?.extractedPersonalData?.dob || '',
      gender: responseCustomerData?.extractedPersonalData?.gender || '',
      idMission: {
        sessionId: sessionId || tempSessionId,
        score: parseInt(resultData?.verificationResultCode || '0', 10),
        message: resultData?.verificationResult || '',
        uniqueRequestId: resultData?.uniqueRequestId || '',
        verificationResultId: resultData?.verificationResultId || '',
        requestId: data?.status?.requestId || '',
      },
      address: {
        country: responseCustomerData?.extractedIdData?.country || 'MÉXICO',
        state: responseCustomerData?.parsedAddress?.province || '',
        city: responseCustomerData?.parsedAddress?.municipality || '',
        postalCode: responseCustomerData?.parsedAddress?.postalCode || '',
        neighborhood: responseCustomerData?.parsedAddress?.neighborhood || '',
        street: responseCustomerData?.parsedAddress?.streetName || '',
        number: responseCustomerData?.parsedAddress?.streetNumber || '',
      },
    };
    Debugger.info('Our mapping -> firstName:', formatData.firstName, 'lastName:', formatData.lastName);

    if (!formatData?.curp || !formatData?.id || !formatData?.firstName || !formatData?.lastName || !formatData?.birthDate || !formatData?.gender) {
      error = literals.verification.errorsIDMission.InvalidData;
    }

    if (
      logOnCapture
      || getIdMissionLogTiming() === ID_MISSION_LOG_TIMING.AFTER_CAPTURE
    ) {
      createIdMissionLog(buildIdMissionLogPayload(formatData));
    }

    if (error && ![ENVIRONMENT_TYPE.PREPRODUCTION, ENVIRONMENT_TYPE.DEVELOPMENT].includes(window.ENVIROMENT)) {
      setSessionId(null);
      onEvent(
        GA_REGISTRATION_ACTIONS.EVENT,
        GA_REGISTRATION_LABELS.CAPTURE_FAILED,
      );
      onError(error || literals.verification.errorsIDMission.Generic);
      return;
    }
    onEvent(
      GA_REGISTRATION_ACTIONS.EVENT,
      GA_REGISTRATION_LABELS.CAPTURE_SUCCESS,
    );
    onContinue(formatData);
  };

  const handleOpenIdMission = () => {
    onEvent(GA_REGISTRATION_ACTIONS.EVENT, GA_REGISTRATION_LABELS.CAPTURE_STARTED);
    dispatch(createIdMissionSession()).then((newSessionId) => {
      setSessionId(newSessionId);
      if (FAKE_VALUE) {
        handleComplete(getIDMissionResponse, newSessionId);
      }
    }).catch(() => {
      onError(literals.verification.errorIdMissionSession);
    });
  };

  const handleCameraPermissions = () => {
    if (cameraPermissions) {
      setCameraPermissions(false);
    } else {
      getCameraPermission()
        .then((permission) => {
          if (permission) {
            setCameraPermissions(true);
          } else {
            // En navegador: mensaje específico explicando cómo habilitar el permiso
            // o descargar la app. En WebView nativo: mensaje original.
            const msg = !platform.isNativeApp
              ? (literals.verification.errorCameraPermissionsBrowser
                || literals.verification.errorCameraPermissions)
              : literals.verification.errorCameraPermissions;
            onError(msg);
          }
        });
    }
  };

  const handleLocationPermissions = () => {
    if (locationPermissions) {
      setLocationPermissions(false);
    } else {
      getLocationPermission()
        .then((permission) => {
          if (permission) {
            setLocationPermissions(true);
          } else {
            onError(literals.verification.errorLocationPermissions);
          }
        });
    }
  };

  return (
    <div className='full-screen-container'>
      <div className='screen-content'>
        <StepHeader step={headerStep} total={headerTotal} onEvent={onEvent} />
        <p className='font-bold text-primary mb-6 text-xl'>{literals.verification.verifyIdentity}</p>
        <p className='font-black mb-2'>{literals.verification.prepareYourIdentity}</p>
        <p className='mb-2'>{literals.verification.scanInfo}</p>
        <ul className='mb-2'>
          <li className='mb-1'>{literals.verification.scanDocument}</li>
          <li className='mb-1'>{literals.verification.scanFace}</li>
        </ul>
        <p className='mb-6'>{literals.verification.scanInfo2}</p>
        <p className='mb-2 underline font-bold'>{literals.verification.weRecommend}</p>
        <ul className='mb-6'>
          <li className='mb-1'>{literals.verification.clearLocation}</li>
          <li className='mb-1'>{literals.verification.avoidShadows}</li>
          <li className='mb-1'>{literals.verification.keepFaceVisible}</li>
          <li>{literals.verification.adjustINE}</li>
        </ul>
        {sessionId && !FAKE_VALUE ? (
          <div className='id-mission-modal'>
            <CustomerIdAndBiometricsEnrollment
              lang='es'
              sessionId={sessionId}
              enrollmentId={sessionId}
              classNames={{
                idCapture: {
                  container: 'id-mission-container',
                  loadingOverlay: {
                    container: 'id-mission-loading-overlay',
                  },
                },
              }}
              deduplicationEnabled
              verifyIdWithExternalDatabases
              onComplete={handleComplete}
              onUserCancel={() => handleCancel(GA_REGISTRATION_LABELS.CAPTURE_CANCELLED_INTRO)}
              onExitCapture={() => handleCancel(GA_REGISTRATION_LABELS.CAPTURE_CANCELLED_CAPTURE)}
              onSelfieCaptureModelError={handleError}
              onCameraAccessDenied={handleCameraAccessDenied}
              onExitAfterFailure={handleError}
            />
          </div>
        ) : null}
      </div>
      <div className='screen-actions'>
        <div className='user-permissions'>
          <div className='flex items-center justify-between gap-4 mb-4'>
            <div className='flex items-center gap-1' onClick={handleCameraPermissions}>
              <div className='switch-icon'>
                <CameraIcon className='icon-primary' height={22} />
              </div>
              <p className='mb-0'>{literals.verification.cameraPermissions}</p>
            </div>
            <Switch
              checked={cameraPermissions}
              onChange={handleCameraPermissions}
            />
          </div>
          <div className='flex items-center justify-between gap-4 mb-6'>
            <div className='flex items-center gap-1' onClick={handleLocationPermissions}>
              <div className='switch-icon'>
                <LocationIcon className='icon-primary' height={26} />
              </div>
              <p className='mb-0'>{literals.verification.locationPermissions}</p>
            </div>
            <Switch
              checked={locationPermissions}
              onChange={handleLocationPermissions}
            />
          </div>
        </div>
        <Button onClick={handleOpenIdMission} disabled={!cameraPermissions || !locationPermissions}>{literals.common.next}</Button>
      </div>
    </div>
  );
};

ClientIdentityValidation.propTypes = {
  literals: PropTypes.object.isRequired,
  onContinue: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  onEvent: PropTypes.func.isRequired,
  headerStep: PropTypes.number.isRequired,
  headerTotal: PropTypes.number.isRequired,
  logOnCapture: PropTypes.bool,
};

export default ClientIdentityValidation;
